import { Event, useMetrics, useStore } from '@mirai/data-sources';
import { ServiceFeatures } from '@mirai/services';
import { useDevice } from '@mirai/ui';
import { useEffect, useState } from 'react';

import { getConsentMode, getPerformanceLabel, uuid } from './helpers';
import { trackExperiment } from './helpers/trackExperiment';
import { CONSENT_MODE_ANSWERED, CONSENT_MODE_REQUIRED } from './PerformanceManager.constants';
import { EVENTS } from './PerformanceManager.events';
import { SCRIPTS } from './PerformanceManager.scripts';
import { version } from '../../../package.json';
import { EVENT as COMPONENTS_EVENT } from '../../components/helpers';
import { COMPONENTS, PERFORMANCE } from '../../Core.constants';
import { fetchUrlParams, getPerformance, loadScript } from '../../helpers';
import { getSuperProps } from '../../helpers/getSuperProps';

const MAX_COUNTER = 5;
const { COMPARE, GTM } = PERFORMANCE || {};

let counter = 0;
let memoizedPendingEvents = [];

const PerformanceManager = () => {
  const device = useDevice();
  const { getDistinctId, register, track } = useMetrics();
  const {
    value: { performance: storePerformance = [], ...store },
  } = useStore();

  const [consentMode, setConsentMode] = useState(getConsentMode());
  const [metaEventId] = useState(uuid);
  const [performance, setPerformance] = useState(storePerformance);

  const { components, finder: { place } = {}, hotel = {}, id: instanceId, locale, session, type } = store;

  useEffect(() => {
    if (
      !device.isMobile ||
      !Object.keys(components || {}).some((component) => Object.values(COMPONENTS).includes(component)) ||
      !ServiceFeatures.get('metrics', instanceId) ||
      store.urlParams?.disableMetrics ||
      window.IS_PLAYWRIGHT ||
      window.location.host === 'monalisa.mirai.com'
    )
      return;

    const timeout = setTimeout(() => {
      if (getDistinctId?.()) trackExperiment(getSuperProps({ store, engineVersion: version }), { register });
    }, 0);

    return () => clearTimeout(timeout);
  }, [components, device.isMobile, getDistinctId, instanceId, register, store]);

  useEffect(() => {
    const metrics = ServiceFeatures.get('metrics', instanceId);

    const handleMetrics = ({ id, ...props }) => {
      if (!id || !metrics || window.IS_PLAYWRIGHT || window.location.host === 'monalisa.mirai.com') return;

      const event = id.toUpperCase().trim();
      track(event, {
        ...props,
        ...getSuperProps({ store, engineVersion: version }),
        locale,
        mobile: device.mobile,
        session,
        type,
      });
    };

    Event.subscribe(COMPONENTS_EVENT.METRICS, handleMetrics);

    return () => Event.unsubscribe(COMPONENTS_EVENT.METRICS, handleMetrics);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  useEffect(() => {
    if (consentMode) return;

    const interval = setInterval(async () => {
      counter !== undefined && counter++;
      const nextConsentMode = getConsentMode();
      const isGtm = (provider) => [COMPARE, GTM].includes(provider);

      if (
        (nextConsentMode === undefined &&
          memoizedPendingEvents.filter(({ provider } = {}) => isGtm(provider)).length === 0) ||
        (!nextConsentMode && counter < MAX_COUNTER)
      )
        return;

      const nextMemoizedPendingEvents = [];
      memoizedPendingEvents.forEach((event = {}) => {
        const { provider } = event;
        const execute =
          nextConsentMode ||
          (counter >= MAX_COUNTER &&
            CONSENT_MODE_ANSWERED.includes(provider) &&
            (nextConsentMode === false || isGtm(provider)));
        const requeue = nextConsentMode === undefined && !isGtm(provider);

        if (execute) {
          executeEvent(event, nextConsentMode);

          if (!nextConsentMode && !isGtm(provider)) nextMemoizedPendingEvents.push(event);
        } else if (!execute || requeue) {
          nextMemoizedPendingEvents.push(event);
        }
      });
      memoizedPendingEvents = nextMemoizedPendingEvents;
      if (nextConsentMode !== undefined) {
        counter = undefined;
        setConsentMode(nextConsentMode);
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentMode]);

  useEffect(() => {
    const { config, finder: { place: { id: [hotelId] = [], isHotel } = {} } = {} } = store;

    if (place) {
      setPerformance(getPerformance(config, isHotel ? hotelId : undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);

  useEffect(() => {
    const handleEvent = ({ event, ...value }) => {
      if (performance.length === 0) return;

      const currentConsentMode = getConsentMode();

      const availableProviders = performance
        .map(({ type } = {}) => type)
        .filter((type) => !(!currentConsentMode && CONSENT_MODE_ANSWERED.includes(type)))
        .filter((type) => currentConsentMode || !CONSENT_MODE_REQUIRED.includes(type));

      Object.entries(EVENTS[event]).forEach(([propProvider, callback = () => {}]) => {
        const provider = parseInt(propProvider);
        const props = { callback, event, provider, value };

        if (!currentConsentMode && !availableProviders.includes(provider)) {
          if (counter === undefined && CONSENT_MODE_ANSWERED.includes(provider)) {
            executeEvent(props);
          } else if (!memoizedPendingEvents.some((item) => item.event === event && item.provider === provider)) {
            memoizedPendingEvents.push(props);
          }
        } else executeEvent(props, currentConsentMode);
      });
    };

    Object.keys(EVENTS).forEach((event) => Event.subscribe(event, handleEvent));

    return () => Object.keys(EVENTS).forEach((event) => Event.unsubscribe(event, handleEvent));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, performance, store]);

  const executeEvent = ({ provider, callback, value, event } = {}, nextConsentMode = consentMode) => {
    loadScripts(
      performance.filter(({ type } = {}) => type === provider && type !== COMPARE),
      nextConsentMode,
    ).then(() => {
      const config = performance.find(({ type }) => type === provider);
      const response = callback({ ...value, ...config, device, metaEventId, store });
      if (!response) return;

      const label = `⚡️ ${getPerformanceLabel(provider)} ${event}`;
      // eslint-disable-next-line no-console
      response.then ? response.then((value) => console.log(label, value)) : console.log(label, response);

      if (provider === GTM) {
        window.Mirai.urlParameters = fetchUrlParams(location.search);
        loadScripts(
          performance.filter(({ type } = {}) => type === COMPARE),
          nextConsentMode,
        );
      }
    });
  };

  const loadScripts = async (performance = [], propConsentMode = consentMode) => {
    const loadedComponents = Object.keys(components);

    const scripts = [];
    performance
      .filter(({ type } = {}) => propConsentMode === true || !CONSENT_MODE_REQUIRED.includes(type))
      .forEach(({ type, instances: propInstances = [], gtgEnabled } = {}) => {
        const { components = [], init = () => {}, once = false } = SCRIPTS[type] || {};

        if (components.length === 0 || components.some((item) => loadedComponents.includes(item))) {
          const instances = once ? [propInstances[0]] : propInstances;
          scripts.push(
            ...instances.map((config = {}, index) => ({
              domain: `performance-${getPerformanceLabel(type)}`,
              id: index + 1,
              ...init({ ...config, hotel, index, instanceId, gtgEnabled, loadedComponents, place }),
              type,
            })),
          );
        }
      });

    for (let index = 0; index < scripts.length; index++) {
      await loadScript(scripts[index]).catch(() => {});
    }
  };

  return null;
};

export { PerformanceManager };
